<template>
    <md-dialog md-open-from="#manageAffiliation" md-close-to="#manageAffiliation" ref="manageAffiliation" @open="fixContent" @close="startOver" class="manage-affiliation-modal manageAffiliation-md-dialog" :class="{ 'show-md-dark-mode': showMode }">
        <md-dialog-title>
            <div class="modal-title" v-if="confirmPurchase">Confirm Purchase</div>
            <div class="modal-title" v-else>Manage Active Users</div>
        </md-dialog-title>
        <md-dialog-content class="manage-affiliation-modal-content">
            <div class="confirm-purchase-container" v-if="confirmPurchase">
                <div>
                        <div class="confirm-text">You are adding {{licensesToAdd}} users at a cost of ${{(licensesToAdd * seatPricePerYear).toFixed(2)}} per year</div>
                        <!-- This takes ~9 seconds against Stripe, and a card challenge can
                             add more. Without this the dialog just sat there and read as
                             frozen — Josh, 18 Sept 2026. -->
                        <div v-if="purchaseWorking" class="purchase-progress">
                            <div class="purchase-progress-bar"><span></span></div>
                            <div class="purchase-progress-text">Contacting your bank. This can take a few seconds — please don't close this window.</div>
                        </div>
                        <p v-if="purchaseError" class="md-error">{{ purchaseError }}</p>
                        <md-button
                            v-if="purchaseError && purchaseErrorIsPaymentProblem"
                            class="md-raised"
                            @click="openManageBilling">Update payment method</md-button>
                        <md-button class="md-raised" :disabled="purchaseWorking" @click="confirmPurchaseSubmit">
                            {{ purchaseWorking ? 'Working…' : 'Confirm' }}
                        </md-button>
                        <md-button class="md-raised" :disabled="purchaseWorking" @click="toggleConfirmPurchase">Cancel</md-button>
                    </div>
            </div>
            <div v-else>
                <div class="edit-your-institution">
                    <form novalidate @submit.stop.prevent="submit" v-on:keyup.stop.prevent="keyPress">
                        <md-input-container>
                            <label for="your-institution">Your institution</label>
                            <md-input v-model="institution_name" ref="input_institution_name" required placeholder="Institution Name"></md-input>
                        </md-input-container>
                    </form>
                    <p>(This is how your collaborators will find you.)</p>
                </div>
                <!-- Invite a user to affiliate (plain markup — no new vue-material) -->
                <div class="affiliate-invite" :class="{'show-md-dark-mode': showMode}" v-if="opened">
                    <div class="affiliate-invite-title">Invite a user to affiliate</div>
                    <div class="affiliate-invite-form">
                        <input type="email" v-model="inviteEmail" placeholder="Email address" class="affiliate-input" @keyup.enter="sendInvite" />
                        <input type="text" v-model="inviteName" placeholder="Name (optional)" class="affiliate-input" />
                        <button type="button" class="affiliate-btn" :disabled="inviting || !inviteEmail" @click="sendInvite">Send Invite</button>
                    </div>
                    <div v-if="inviteError" class="affiliate-invite-msg affiliate-invite-error">{{ inviteError }}</div>
                    <div v-if="inviteNotice" class="affiliate-invite-msg affiliate-invite-notice">{{ inviteNotice }}</div>

                    <div v-if="sentInvites.length" class="affiliate-pending">
                        <div class="affiliate-pending-title">Pending invitations</div>
                        <div v-for="inv in sentInvites" :key="inv.id" class="affiliate-pending-row">
                            <span class="affiliate-pending-email">{{ inv.email }}<span v-if="inv.name"> ({{ inv.name }})</span><span v-if="inv.status === 'failed'" class="affiliate-pending-failed"> &middot; Couldn't join: has their own subscription</span></span>
                            <span class="affiliate-pending-actions">
                                <button v-if="inv.status !== 'failed'" type="button" class="affiliate-link-btn" :disabled="resendCooldown[inv.id]" @click="resendInvite(inv)">{{ resendCooldown[inv.id] ? 'Sent' : 'Resend' }}</button>
                                <button type="button" class="affiliate-link-btn affiliate-link-danger" @click="cancelInvite(inv)">{{ inv.status === 'failed' ? 'Remove' : 'Cancel' }}</button>
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Active license distribution -->
                <div class="affiliations-list" v-if="opened">
                    <affiliate-slot v-for="slotNumber in availableSlots" :key="slotNumber" :slotNumber="slotNumber" :affiliateUsers="affiliateUsers" :slotAffiliate="activeAffiliates[slotNumber-2]" @updateSlot="updateSlotAffiliate"></affiliate-slot>
                </div>

                <!-- Affiliated users roster with removal -->
                <div class="affiliate-roster" :class="{'show-md-dark-mode': showMode}" v-if="opened">
                    <div class="affiliate-roster-title">Affiliated users</div>
                    <div v-if="rosterUsers.length">
                        <div v-for="ru in rosterUsers" :key="ru.uid" class="affiliate-roster-row">
                            <span class="affiliate-roster-name">{{ ru.label }}</span>
                            <span class="affiliate-roster-state" :class="{ 'is-active': ru.active && !ru.selfPays, 'is-self-pay': ru.selfPays }" :title="ru.selfPays ? 'This member pays for their own subscription, so they do not use one of your licenses.' : null">{{ ru.selfPays ? 'Own subscription' : (ru.active ? 'Active license' : 'Inactive') }}</span>
                            <button type="button" class="affiliate-link-btn affiliate-link-danger" @click="removeAffiliate(ru)">Remove</button>
                        </div>
                    </div>
                    <p v-else class="affiliate-roster-empty">No one's affiliated yet — invite your team above.</p>
                </div>
                <div class="affiliations-footer" :class="{'show-md-dark-mode': showMode, 'is-adding-licenses': addLicenseToggle}">
                    <div class="affiliations-footer-total-license" v-if="!addLicenseToggle">Total Licenses: {{licenseCount}}</div>
                    <div class="add-licenses-button-container">
                        <md-button class="md-raised is-ghost" @click="addLicenses" v-if="!addLicenseToggle">Adjust Total Licenses</md-button>
                        <div class="footer-actions-right" v-if="!addLicenseToggle">
                            <md-button class="md-raised is-ghost" @click="cancel">Cancel</md-button>
                            <md-button class="md-raised" :disabled="institution_name === ''" @click="confirmChanges">Confirm Changes</md-button>
                        </div>
                    </div>
                    <div class="edit-your-institution-actions">
                    </div>
                    <div class="add-licenses-container" v-if="addLicenseToggle">
                        <div class='add-licenses-text'>
                            <div>ADD ADDITIONAL USERS AT ${{ seatPricePerYear.toFixed(2) }} PER USER/PER YEAR</div>
                            <div v-if="isDualMultiSeatPlan" style="font-size: 12px; margin-top: 4px;">
                                Dual seat pricing. Seat holders must also be invited in The Notes List.
                            </div>
                        </div>
                        <div class="add-licenses-buttons">
                            <md-button class="md-raised is-ghost is-icon" @click="minusNewLicenses">-</md-button>
                            <md-button class="md-raised is-ghost is-icon" @click="plusNewLicenses">+</md-button>
                            <md-button class="md-raised" @click="toggleConfirmPurchase">Submit</md-button>
                            <md-button class="md-raised is-ghost" @click="addLicenses">Cancel</md-button>
                        </div>
                        <div class='add-licenses-text'>
                            <div>ADDITIONAL USERS: {{licensesToAdd}} &nbsp;</div>
                            <div>TOTAL USERS: {{newTotalLicenses}}</div>
                        </div>
                    </div>
                </div>
            </div>
        </md-dialog-content>
    </md-dialog>
</template>

<script>
import Vue from 'vue';
import firebase from 'firebase/app';
import { mapGetters } from 'vuex';
import { db } from '@/main';
import AffiliateSlot from '@/modals/manage-affiliation/affiliate-slot';
import { UPDATE_ADDITIONAL_LICENSES_URL, CONFIRM_ADDITIONAL_LICENSES_URL, CLOUD_FUNCTIONS_BASE } from '@/lib/URLs';
import { STRIPE_PUB_KEY } from '@/lib/global';
import ensureStripeJsLoaded from '@/lib/load-stripe-js';

import { sendMail } from '@/lib/sendMail';

const ManageAffiliation = {
    name: 'manageAffiliation',
    data: function() {
        return {
            institution_name: '',
            activeAffiliates: [],
            affiliateUsers: {},
            // Snapshot of affiliateUsers at open — used to persist only the keys
            // the admin actually changed (per-key diff), so a wholesale write
            // can't resurrect a just-removed user or clobber a concurrent accept.
            originalAffiliateUsers: {},
            slotsModified: false,
            opened: false,
            addLicenseToggle: false,
            newLicenseCount: 0,
            plusOrMinusState: '+',
            confirmPurchase: false,
            // Invite form
            inviteEmail: '',
            inviteName: '',
            inviting: false,
            inviteError: '',
            inviteNotice: '',
            inviteNoticeTimer: null,
            sentInvites: [],
            sentInvitesUnsub: null,
            resendCooldown: {},
            // Affiliated-users roster (resolved names)
            rosterUsers: [],
            purchaseWorking: false,
            purchaseError: ''
        };
    },
    computed: {
        ...mapGetters([
            'user',
            'users',
            'showMode',
            'userByEmail',
            'userByUID',
            'affiliateInvitationPending'
        ]),
        availableSlots() {
            const totalLicenses = this.user.total_licenses || 5;
            const slots = [];
            for (let i = 0; i < totalLicenses - 1; i++) {
                slots.push(i + 2);
            }
            return slots;
        },
        licenseCount() {
            const totalLicenses = this.user.total_licenses || 5;
            return totalLicenses;
        },
        newTotalLicenses() {
            const int = Number.parseInt(this.user.total_licenses, 10);
            const totalLicenses = int || 5;
            const newTotal = totalLicenses + this.newLicenseCount;
            return newTotal;
        },
        licensesToAdd() {
            const unsignedLicenseCount = Math.abs(this.newLicenseCount);
            return unsignedLicenseCount;
        },
        // Only offer the billing portal when the card is the problem. Our own
        // errors ("something went wrong on our end") must not send a customer
        // off to their bank over a bug they cannot see.
        purchaseErrorIsPaymentProblem() {
            const e = (this.purchaseError || '').toLowerCase();
            return e.includes('card') || e.includes('payment method') || e.includes('declined');
        },
        getAddExistingAdditionalLicenses() {
            const totalLicenses = this.user.total_licenses || 5;
            let text = '';
            if (totalLicenses > 5) {
                text = ` + ${totalLicenses - 5}`;
            }
            return text;
        },
        plusOrMinus() {
            return this.plusOrMinusState;
        },
        isDualMultiSeatPlan() {
            return !!(
                this.user &&
                this.user.noteslist_integration_source === 'dual' &&
                (this.user.subscription_level === 'dual_educational' ||
                    this.user.subscription_level === 'dual_institution')
            );
        },
        seatPricePerYear() {
            return this.isDualMultiSeatPlan ? 59.99 : 34.99;
        },
        // Stable signal that changes whenever the live-bound user.affiliateUsers
        // map changes (a user affiliating/removing while the modal is open). The
        // activeUser vuex binding is live, so this fires without a page refresh.
        liveAffiliateKey() {
            const map = (this.user && this.user.affiliateUsers) || {};
            return Object.keys(map).sort().map(uid => `${uid}:${map[uid] ? 1 : 0}`).join(',');
        }
    },
    watch: {
        liveAffiliateKey() {
            if (this.opened) this.reconcileFromLive();
        }
    },
    methods: {
        open() {
            this.addLicenseToggle = false;
            this.confirmPurchase = false;
            this.purchaseError = '';
            this.$refs.manageAffiliation.open();
        },
        closeDialog() {
            this.addLicenseToggle = false;
            this.confirmPurchase = false;
            this.purchaseError = '';
            this.$refs.manageAffiliation.close();
        },
        fixContent() {
            this.opened = true;
            this.slotsModified = false;
            this.institution_name = this.user.institution_name || '';
            this.affiliateUsers = { ...this.user.affiliateUsers };
            this.originalAffiliateUsers = { ...this.user.affiliateUsers };
            this.activeAffiliates = Object.keys(this.user.affiliateUsers || {}).filter(uid => this.user.affiliateUsers[uid]);
            const totalLicenses = this.user.total_licenses || 5;
            this.addLicenseToggle = false;
            for (let i = 0; i < totalLicenses - this.activeAffiliates.length; i++) {
                this.activeAffiliates.push('');
            }
            this.inviteEmail = '';
            this.inviteName = '';
            this.inviteError = '';
            this.inviteNotice = '';
            this.subscribeSentInvites();
            this.resolveRoster();
            Vue.material.setCurrentTheme(this.showMode ? 'show-mode' : 'default');
        },
        startOver() {
            this.opened = false;
            this.unsubscribeSentInvites();
            this.clearNoticeTimer();
            Vue.material.setCurrentTheme(this.showMode ? 'show-mode' : 'default');
        },
        cancel() {
            this.$refs.manageAffiliation.close();
        },
        confirmChanges() {
            const previousAffiliateUsers = { ...(this.user.affiliateUsers || {}) };
            const nextAffiliateUsers = { ...this.affiliateUsers };
            this.$nextTick(() => {
                // Persist institution_name plus ONLY the affiliateUsers keys the
                // admin changed in the slot UI — a per-key dotted-path diff, not
                // a wholesale map write. This avoids clobbering a key that was
                // accepted/changed concurrently and avoids resurrecting a key
                // removed via the Remove button (which deletes it immediately and
                // from the local snapshot).
                const update = { institution_name: this.institution_name };
                const current = this.affiliateUsers || {};
                const original = this.originalAffiliateUsers || {};
                Object.keys(current).forEach((uid) => {
                    if (current[uid] !== original[uid]) {
                        update['affiliateUsers.' + uid] = current[uid];
                    }
                });
                Object.keys(original).forEach((uid) => {
                    if (!(uid in current)) {
                        update['affiliateUsers.' + uid] = firebase.firestore.FieldValue.delete();
                    }
                });
                this.$store.dispatch('updateUser', {
                    uid: this.user.uid,
                    update: update
                }).then(async () => {
                    if (this.isDualMultiSeatPlan) {
                        try {
                            await this.$store.dispatch('syncNotesListAffiliateSeats', {
                                previousAffiliateUsers,
                                affiliateUsers: nextAffiliateUsers
                            });
                        } catch (err) {
                            console.error(
                                '[noteslist-integration] affiliate seat sync failed:',
                                err
                            );
                            this.$toast.error(
                                'Active users were saved, but syncing seats to Notes List failed. Please try again, or contact support if this continues.'
                            );
                            this.cancel();
                            return;
                        }
                    }
                    this.cancel();
                    this.$toast.success('Active users have been successfully updated.');
                });
            });
        },
        subscribeSentInvites() {
            this.unsubscribeSentInvites();
            if (!this.user || !this.user.uid) return;
            this.sentInvitesUnsub = db.collection('affiliateInvitations')
                .where('adminUid', '==', this.user.uid)
                .onSnapshot((snap) => {
                    this.sentInvites = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                }, (err) => { console.error('Sent invites listener error:', err); });
        },
        unsubscribeSentInvites() {
            if (this.sentInvitesUnsub) {
                this.sentInvitesUnsub();
                this.sentInvitesUnsub = null;
            }
            this.sentInvites = [];
        },
        async resolveRoster() {
            const map = this.user.affiliateUsers || {};
            const keys = Object.keys(map);
            const resolved = await Promise.all(keys.map(async (uid) => {
                const u = await this.userByUID(uid);
                return {
                    uid: uid,
                    label: (u && (u.username || u.email)) || uid,
                    active: Boolean(map[uid]),
                    // Pays for their own subscription: affiliated, but never
                    // takes a seat. Rendered as its own state so the admin can
                    // see why they aren't in the Active User dropdowns.
                    selfPays: Boolean(u && u.hasOwnSubscription)
                };
            }));
            this.rosterUsers = resolved;
        },
        // Merge live changes to user.affiliateUsers into the open modal without
        // clobbering the admin's unsaved slot edits. Called by the liveAffiliateKey
        // watcher so a user affiliating/being removed shows up with no refresh.
        reconcileFromLive() {
            const live = (this.user && this.user.affiliateUsers) || {};
            // Keys that appeared externally (a new affiliation) → make them known
            // locally so they're offered in the slot dropdowns, and baseline them
            // so Confirm's per-key diff doesn't try to re-write them.
            Object.keys(live).forEach((uid) => {
                if (!(uid in this.originalAffiliateUsers)) {
                    this.$set(this.originalAffiliateUsers, uid, live[uid]);
                    if (!(uid in this.affiliateUsers)) {
                        this.$set(this.affiliateUsers, uid, live[uid]);
                    }
                    // Surface a newly-activated affiliate in the first open slot.
                    if (live[uid] && this.activeAffiliates.indexOf(uid) === -1) {
                        const emptyIdx = this.activeAffiliates.indexOf('');
                        if (emptyIdx !== -1) this.$set(this.activeAffiliates, emptyIdx, uid);
                    }
                }
            });
            // Keys removed externally (e.g. the member self-removed) that the admin
            // hasn't locally edited → drop them from the working copy + slots.
            Object.keys(this.originalAffiliateUsers).forEach((uid) => {
                if (!(uid in live)) {
                    const locallyChanged = this.affiliateUsers[uid] !== this.originalAffiliateUsers[uid];
                    if (!locallyChanged) {
                        this.$delete(this.affiliateUsers, uid);
                        const idx = this.activeAffiliates.indexOf(uid);
                        if (idx !== -1) this.$set(this.activeAffiliates, idx, '');
                    }
                    this.$delete(this.originalAffiliateUsers, uid);
                }
            });
            this.resolveRoster();
        },
        generateAffiliateEmailBody(name) {
            const host = document.URL.split('/')[2] || 'app.thecuelist.com';
            const appLink = `https://${host}`;
            const greeting = name ? `Hi ${name},` : 'Hi,';
            return `<p>${greeting}</p>
                <p>You've been invited by <strong>${this.user.username || this.user.email}</strong> to affiliate your Cuelist account with <strong>${this.user.institution_name || 'their institution'}</strong>.</p>
                <p>Affiliating gives you a Cuelist license paid for by the institution — it's free to you. Cuelist is web-based software for creating, editing and sharing cues and notes on a PDF script in real time.</p>
                <p>If you already use Cuelist, sign in at <a href="${appLink}">${host}</a> and accept the invitation from your dashboard.<br/>
                   If you're new to Cuelist, click <a href="https://thecuelist.com">here</a> to sign up for free — then accept the invitation from your dashboard.</p>
                <p>Questions? Reach out to <strong>${this.user.username || ''}</strong> at <a href="mailto:${this.user.email}">${this.user.email}</a>, or email <a href="mailto:support@thecuelist.com">support@thecuelist.com</a>.</p>
                <p>Thanks for using Cuelist!</p>`;
        },
        // Transient notice that clears itself after 15s (used for advisory notes
        // like a self-payer heads-up, which shouldn't linger indefinitely).
        flashNotice(msg) {
            this.inviteNotice = msg;
            this.clearNoticeTimer();
            this.inviteNoticeTimer = setTimeout(() => {
                this.inviteNotice = '';
                this.inviteNoticeTimer = null;
            }, 15000);
        },
        clearNoticeTimer() {
            if (this.inviteNoticeTimer) {
                clearTimeout(this.inviteNoticeTimer);
                this.inviteNoticeTimer = null;
            }
        },
        postInviteEmail(email, name) {
            const postData = {
                email: email,
                subject: `You're invited to affiliate with ${this.user.institution_name || 'an institution'} on Cuelist`,
                content: this.generateAffiliateEmailBody(name)
            };
            // Separate leg from the invite doc. Email is the ONLY channel for an
            // invitee without an account yet, so surface a send failure rather
            // than silently reporting success.
            sendMail(postData).catch((error) => {
                console.error('Affiliate invite email failed:', error);
                this.flashNotice('Invitation created, but the email did not send. Use Resend to try again.');
            });
        },
        async sendInvite() {
            this.inviteError = '';
            this.inviteNotice = '';
            this.clearNoticeTimer();
            const email = (this.inviteEmail || '').trim().toLowerCase();
            if (!email) { this.inviteError = 'Enter an email address.'; return; }
            if (email === (this.user.email || '').toLowerCase()) {
                this.inviteError = 'You cannot invite yourself.';
                return;
            }
            this.inviting = true;
            try {
                const existing = await this.userByEmail(email);
                if (existing) {
                    // Decided server-side (lookupUserByEmail): the same two checks this
                    // used to make against the invitee's whole record.
                    const alreadyAffiliated = existing.affiliatedWithCaller === true;
                    if (alreadyAffiliated) {
                        this.inviteError = `${existing.username || email} is already affiliated with your institution.`;
                        this.inviting = false;
                        return;
                    }
                    // Josh, 23 Sept 2026: someone with their own live subscription
                    // cannot join an institution's license. Refuse here, before any
                    // invite or email, and say why. A pending cancellation is allowed
                    // (decided server-side in lookupUserByEmail.blocksAffiliation).
                    if (existing.blocksAffiliation === true) {
                        this.inviteError = `${existing.username || email} already pays for their own Cuelist subscription, so they can't join your license. If they cancel their subscription, you can invite them again.`;
                        this.inviting = false;
                        return;
                    }
                }
                const pending = await this.affiliateInvitationPending(email, this.user.uid);
                if (pending) {
                    this.inviteError = 'An invitation has already been sent to this email.';
                    this.inviting = false;
                    return;
                }
                await this.$store.dispatch('inviteToAffiliate', { email: email, name: this.inviteName });
                this.postInviteEmail(email, this.inviteName);
                this.inviteEmail = '';
                this.inviteName = '';
                this.$toast.success('Invitation sent.');
            } catch (e) {
                console.error(e);
                this.inviteError = 'Could not create the invitation. Please try again.';
            }
            this.inviting = false;
        },
        resendInvite(inv) {
            if (this.resendCooldown[inv.id]) return;
            this.inviteNotice = '';
            this.postInviteEmail(inv.email, inv.name);
            this.$set(this.resendCooldown, inv.id, true);
            this.$toast.success('Invitation re-sent.');
            setTimeout(() => { this.$set(this.resendCooldown, inv.id, false); }, 5000);
        },
        cancelInvite(inv) {
            this.$store.dispatch('declineAffiliateInvitation', { invitationID: inv.id });
        },
        // The roster holds three states and only ONE of them costs the member
        // anything on removal, so the confirmation has to match:
        //   active && !selfPays  -> holds one of our licenses. Removing it
        //                           deactivates their license immediately.
        //   selfPays             -> affiliated, but pays for their own
        //                           subscription. Removal takes nothing away.
        //   neither              -> affiliated, holds no license. Same.
        // The single message used to warn EVERY removal about losing a license,
        // which was false for two of the three and made the real warning easy to
        // click through.
        removeAffiliate(ru) {
            const org = this.user.institution_name || 'your institution';
            const holdsLicense = Boolean(ru.active) && !ru.selfPays;
            const message = holdsLicense
                ? `${ru.label} is currently using one of your licenses. `
                    + `Removing them from ${org} will deactivate their license immediately, `
                    + 'and they\'ll need a new invitation to rejoin.'
                : `Remove ${ru.label} from ${org}? `
                    + 'They aren\'t using one of your licenses, so their access won\'t change.'
                    + (ru.selfPays ? ' Their own subscription is unaffected.' : '')
                    + ' They\'ll need a new invitation to rejoin.';
            // eslint-disable-next-line no-alert
            if (!window.confirm(message)) {
                return;
            }
            // Admin writes only their own doc (rules block member writes); the CF
            // cascades affiliateInstitution + active_pay to the member. Immediate
            // per-key delete, kept out of the batched Confirm so the CF's diff
            // handles it cleanly and it can't be resurrected on Confirm.
            this.$store.dispatch('updateUser', {
                uid: this.user.uid,
                update: { ['affiliateUsers.' + ru.uid]: firebase.firestore.FieldValue.delete() }
            }).then(() => {
                this.$toast.success(`${ru.label} removed.`);
            });
            // Reflect locally so Confirm Changes and the slot dropdowns don't
            // re-add the removed user.
            this.$delete(this.affiliateUsers, ru.uid);
            this.$delete(this.originalAffiliateUsers, ru.uid);
            this.activeAffiliates = this.activeAffiliates.map(uid => (uid === ru.uid ? '' : uid));
            this.rosterUsers = this.rosterUsers.filter(r => r.uid !== ru.uid);
        },
        keyPress(e) {
            if (e.keyCode === 27) {
                this.$refs.input_institution_name.$el.blur();
            }
        },
        submit() {
            this.$refs.input_institution_name.$el.blur();
        },
        saveInstitution(e) {
            e.preventDefault();
            e.target.blur();
        },
        updateSlotAffiliate({slotNumber, uid}) {
            this.activeAffiliates[slotNumber - 2] = uid;
            this.slotsModified = true;
        },
        addLicenses() {
            if (this.addLicenseToggle) {
                this.addLicenseToggle = false;
            } else {
                this.addLicenseToggle = true;
            }
        },
        plusNewLicenses() {
            this.newLicenseCount++;
            if (this.newLicenseCount >= 0) {
                this.plusOrMinusState = '+';
            }
        },
        // Minus only walks back an unsubmitted addition; it cannot reduce an
        // institution's actual licence count. That is step 50, and it is
        // DELIBERATELY not shipped here.
        //
        // A working version exists on branch feat/seat-reduction-end-of-term
        // (reduction with a floor of max(5, seats in use + 1), plus tests). It is
        // parked because that version reduces IMMEDIATELY with a credit, and Josh
        // wants reductions to take effect at the END OF TERM instead — 17 Sept:
        // shipping the immediate behaviour now would mean changing it under
        // customers a fortnight later. Reduction arrives once, done properly.
        minusNewLicenses() {
            if (this.newLicenseCount <= 0) {
                this.newLicenseCount = 0;
                this.plusOrMinusState = '+';
                return;
            }
            this.newLicenseCount--;
            this.plusOrMinusState = '+';
        },
        toggleConfirmPurchase() {
            if (this.purchaseWorking) return;
            if (this.licensesToAdd !== 0) {
                this.purchaseError = '';
                if (this.confirmPurchase) {
                    this.confirmPurchase = false;
                } else {
                    this.confirmPurchase = true;
                }
            }
        },
        /**
         * Runs the Stripe.js challenge for a seat-resize that came back
         * requiresAction, then asks the backend to finalize it. Never trusts
         * the client's own word that the card was verified — the backend
         * re-checks the invoice against live Stripe state before writing
         * anything (see /confirm-additional-licenses).
         */
        // `subId` is passed in rather than re-read from this.user, because the
        // user doc can change underneath a purchase that is still in flight: the
        // 3DS window drops the subscription to past_due, and the webhook's
        // teardown blanks subscription_id while the customer is still looking at
        // the Visa challenge. Re-reading it here sent sub_id:"" and the confirm
        // call 400'd on a purchase that had actually succeeded (sandbox,
        // 17 Sept 2026, 20:25:07). The backend no longer tears down in that
        // window, but the id we were given at the start is the correct one to
        // finish with regardless of what else moves.
        async handleRequiresAction(clientSecret, subId) {
            if (!clientSecret) {
                this.purchaseError =
                    'Payment verification could not start. Please refresh the page and try again.';
                return false;
            }
            if (!(await ensureStripeJsLoaded())) {
                this.purchaseError =
                    'Payment verification could not start. Please check your connection and try again.';
                return false;
            }
            const stripeJs = window.Stripe(STRIPE_PUB_KEY);
            const confirmResult = await stripeJs.confirmCardPayment(clientSecret);
            if (confirmResult.error) {
                this.purchaseError =
                    confirmResult.error.message || 'Payment verification failed. Please try again.';
                return false;
            }
            try {
                await Vue.http.post(CONFIRM_ADDITIONAL_LICENSES_URL, {
                    sub_id: subId
                });
                return true;
            } catch (err) {
                this.purchaseError =
                    (err && err.body && err.body.error) ||
                    'Verification completed, but we could not finish adding the seats. Please try again.';
                return false;
            }
        },
        // Josh, 18 Sept 2026, on hitting a decline here:
        //   "there is no way to update the card through the add seats mechanic…
        //    they will have to go to the billing site to fix this and that needs
        //    them to exit this modal, open the manage account modal and then click
        //    another button. That is a lot of button presses."
        //
        // Same implementation as upgrade-account.vue's openManageBilling — a
        // POSTed form to /create-portal-session, which is the route that actually
        // works. Deliberately NOT updateCard(): that method exists in
        // upgrade-account.vue but nothing calls it, and it needs Stripe Elements
        // mounted in that modal. The portal is where cards are genuinely updated.
        openManageBilling() {
            if (!this.user || !this.user.stripe_id) {
                this.purchaseError = 'No billing account found. Please contact support.';
                return;
            }
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = `${CLOUD_FUNCTIONS_BASE}/create-portal-session`;
            form.target = '_blank';
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'customer_id';
            input.value = this.user.stripe_id;
            form.appendChild(input);
            document.body.appendChild(form);
            form.submit();
            document.body.removeChild(form);
        },
        async confirmPurchaseSubmit() {
            if (this.purchaseWorking) return;
            this.purchaseWorking = true;
            this.purchaseError = '';
            // Captured ONCE, before anything can move it. See handleRequiresAction.
            const subId = this.user.subscription_id;
            try {
                const response = await Vue.http.post(UPDATE_ADDITIONAL_LICENSES_URL, {
                    new_license_count: this.newTotalLicenses,
                    sub_id: subId,
                    customer_id: this.user.stripe_id,
                    old_license_count: this.user.total_licenses
                });
                const body = response.body || {};
                if (body.requiresAction) {
                    const confirmed = await this.handleRequiresAction(body.clientSecret, subId);
                    if (!confirmed) {
                        return;
                    }
                }
                this.$toast.success('Active users have been successfully updated.');
                this.confirmPurchase = false;
                this.addLicenseToggle = false;
                this.newLicenseCount = 0;
            } catch (err) {
                this.purchaseError =
                    (err && err.body && err.body.error) ||
                    (err && err.message) ||
                    'Could not add users. Please try again.';
            } finally {
                this.purchaseWorking = false;
            }
        }
    },
    beforeDestroy() {
        this.unsubscribeSentInvites();
        this.clearNoticeTimer();
    },
    components: {
        'affiliate-slot': AffiliateSlot
    }
};
export default ManageAffiliation;
</script>

<style lang="scss">
/* Manage Active Users modal — restyled to match the account redesign.
   Local design tokens; dark mode via .show-md-dark-mode on the dialog root
   (bound to showMode). No color-mix() — node-sass can't parse it. */
.manage-affiliation-modal {
    --navy: #0d2744;
    --accent: #73d5e3;
    --accent-ink: #12718a;
    --surface: #ffffff;
    --surface-2: #f6f9fc;
    --border: #dde4ee;
    --border-strong: #c9d4e2;
    --text: #13263e;
    --text-muted: #5c6d84;
    --btn-bg: #0d2744;
    --btn-fg: #ffffff;
    --btn-hover: #16375f;
    --danger: #c0392b;
    --ring: 0 0 0 3px rgba(115,213,227,.55);
}
.manage-affiliation-modal.show-md-dark-mode {
    --accent-ink: #73d5e3;
    --surface: #12243a;
    --surface-2: #0e1d30;
    --border: #24384f;
    --border-strong: #33506e;
    --text: #e9f1f8;
    --text-muted: #9fb4cb;
    --btn-bg: #73d5e3;
    --btn-fg: #08202c;
    --btn-hover: #8fe0eb;
    --danger: #f0938c;
}

.manageAffiliation-md-dialog .md-dialog.md-reference { overflow-y: auto; }

.manage-affiliation-modal .md-dialog {
    min-width: 600px;
    max-width: 720px;
    background: var(--surface);
    color: var(--text);
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 24px 70px rgba(6,15,26,.45);
    font-family: system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}
.manage-affiliation-modal .md-dialog-title {
    background: var(--navy);
    color: #fff;
    text-align: left;
    padding: 18px 24px;
}
.manage-affiliation-modal .md-dialog-title .modal-title {
    font-size: 19px;
    font-weight: 700;
    line-height: 1.3;
    letter-spacing: .01em;
}
.manage-affiliation-modal .manage-affiliation-modal-content {
    /* No bottom padding: the sticky footer below provides it. With padding here
       the footer stuck to the padding EDGE, leaving a 22px band underneath it
       where the scrolling slot grid showed through — reported 18 Sept. */
    padding: 22px 24px 0;
    min-height: 150px;
    background: var(--surface);
    color: var(--text);
    scrollbar-width: none;
}

/* Buttons (override vue-material raised look) */
.manage-affiliation-modal .md-button {
    height: auto;
    min-width: 0;
    margin: 4px 6px;
    padding: 8px 15px;
    border-radius: 9px;
    border: 1px solid transparent;
    background: var(--btn-bg);
    color: var(--btn-fg) !important;
    font-size: 13px;
    font-weight: 650;
    text-transform: none;
    box-shadow: none !important;
}
.manage-affiliation-modal .md-button:hover { background: var(--btn-hover); }
.manage-affiliation-modal .md-button.is-ghost {
    background: transparent;
    color: var(--text) !important;
    border-color: var(--border-strong);
}
.manage-affiliation-modal .md-button.is-ghost:hover { background: var(--surface-2); }
.manage-affiliation-modal .md-button.is-icon { min-width: 42px; padding: 10px 0; font-size: 18px; font-weight: 700; }
.manage-affiliation-modal .md-button[disabled] { opacity: .5; }

/* Institution name field */
.manage-affiliation-modal .edit-your-institution { margin: 0 0 6px; }
.manage-affiliation-modal .edit-your-institution p {
    color: var(--text-muted);
    font-size: 14px;
    margin: 8px 0 18px;
}
.manage-affiliation-modal .edit-your-institution p a { color: var(--accent-ink); }

/* Modernize the vue-material inputs/selects into clean bordered fields */
.manage-affiliation-modal .md-input-container {
    border: 1px solid var(--border-strong);
    border-radius: 9px;
    padding: 6px 12px;
    min-height: 44px;
    background: var(--surface-2);
    margin: 4px 0 8px;
}
.manage-affiliation-modal .md-input-container:after { display: none; }
.manage-affiliation-modal .md-input-container label {
    color: var(--text-muted);
    top: 12px;
    left: 12px;
    font-size: 14px;
    line-height: 1;
}
.manage-affiliation-modal .md-input-container.md-has-value label,
.manage-affiliation-modal .md-input-container.md-input-focused label {
    transform: translateY(-20px);
    font-size: 11px;
    color: var(--accent-ink);
    padding: 2px 4px;
    background: var(--surface);
}
.manage-affiliation-modal .md-input-container.md-has-value label:after,
.manage-affiliation-modal .md-input-container.md-input-focused label:after { display: none; }
.manage-affiliation-modal .md-input,
.manage-affiliation-modal .md-input-container input {
    color: var(--text);
    font-size: 15px;
}
.manage-affiliation-modal .md-input-container.md-input-focused { box-shadow: var(--ring); border-color: var(--accent); }

/* Active-license distribution (affiliate-slot selects) — compact 3-up grid with
   a small static caption label (not the tall floating-notch) so a big roster
   needs far less scrolling. */
.manage-affiliation-modal .affiliations-list {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px 10px;
    margin-bottom: 6px;
}
@media (max-width: 560px) {
    .manage-affiliation-modal .affiliations-list { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
.manage-affiliation-modal .affiliations-list .md-input-container {
    position: relative;
    flex: none;
    margin: 0;
    min-height: 0;
    padding: 16px 10px 5px;
    border: 1px solid var(--border-strong);
    border-radius: 8px;
    background: var(--surface-2);
}
.manage-affiliation-modal .affiliations-list .md-input-container label {
    position: absolute;
    top: 5px;
    left: 10px;
    transform: none !important;
    font-size: 10px;
    letter-spacing: .03em;
    text-transform: uppercase;
    color: var(--text-muted);
    font-weight: 600;
    background: transparent;
    line-height: 1;
}
.manage-affiliation-modal .affiliations-list .md-input-container label:after { display: none; }
.manage-affiliation-modal .affiliations-list .affiliate-slot-select-box { font-size: 14px !important; color: var(--text); }
.manage-affiliation-modal .affiliations-list .md-select { min-width: 20px; text-align: left; }
.manage-affiliation-modal .affiliations-list .md-select .md-select-value { padding-right: 0; color: var(--text); }
.manage-affiliation-modal .affiliations-list .md-select:after { display: none; }

/* Invite section */
.manage-affiliation-modal .affiliate-invite {
    margin: 4px 0 18px;
    padding-top: 16px;
    border-top: 1px solid var(--border);
}
.manage-affiliation-modal .affiliate-invite-title {
    font-size: 15px;
    font-weight: 700;
    color: var(--text);
    margin-bottom: 10px;
}
.manage-affiliation-modal .affiliate-invite-form { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.manage-affiliation-modal .affiliate-input {
    flex: 1 1 180px;
    border: 1px solid var(--border-strong);
    border-radius: 9px;
    padding: 10px 12px;
    font-size: 14px;
    background: var(--surface-2);
    color: var(--text);
}
.manage-affiliation-modal .affiliate-input::placeholder { color: var(--text-muted); }
.manage-affiliation-modal .affiliate-input:focus-visible { outline: none; box-shadow: var(--ring); border-color: var(--accent); }
.manage-affiliation-modal .affiliate-btn {
    border: none;
    border-radius: 9px;
    background: var(--btn-bg);
    color: var(--btn-fg);
    font-weight: 650;
    font-size: 14px;
    padding: 11px 18px;
    cursor: pointer;
}
.manage-affiliation-modal .affiliate-btn:hover { background: var(--btn-hover); }
.manage-affiliation-modal .affiliate-btn:disabled { opacity: .5; cursor: default; }
.manage-affiliation-modal .affiliate-invite-msg { font-size: 13px; margin-top: 8px; }
.manage-affiliation-modal .affiliate-invite-error { color: var(--danger); }
.manage-affiliation-modal .affiliate-invite-notice { color: #b8860b; }
.manage-affiliation-modal .affiliate-pending { margin-top: 14px; }
.manage-affiliation-modal .affiliate-pending-title {
    font-size: 12px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase;
    color: var(--text-muted); margin-bottom: 6px;
}
.manage-affiliation-modal .affiliate-pending-row {
    display: flex; align-items: center; justify-content: space-between;
    padding: 6px 0; font-size: 14px; color: var(--text-muted);
}
/* A failed invite's reason is body text; Remove beside it stays the red link, so
   the clickable part is the only red thing (Josh, 23 Sept 2026). */
.manage-affiliation-modal .affiliate-pending-failed { color: var(--text); font-style: normal; font-weight: 600; }
.manage-affiliation-modal .affiliate-pending-email { font-style: italic; }

/* Roster */
.manage-affiliation-modal .affiliate-roster { margin: 0 0 18px; }
.manage-affiliation-modal .affiliate-roster-title { font-size: 15px; font-weight: 700; color: var(--text); margin-bottom: 8px; }
.manage-affiliation-modal .affiliate-roster-row {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 0; border-bottom: 1px solid var(--border);
}
.manage-affiliation-modal .affiliate-roster-name { flex: 1 1 auto; font-size: 14px; color: var(--text); }
.manage-affiliation-modal .affiliate-roster-state { font-size: 12px; color: var(--text-muted); }
.manage-affiliation-modal .affiliate-roster-state.is-active { color: var(--accent-ink); font-weight: 700; }
.manage-affiliation-modal .affiliate-roster-empty { color: var(--text-muted); font-size: 14px; }

/* Link buttons (Resend / Cancel / Remove) */
.manage-affiliation-modal .affiliate-link-btn {
    border: none; background: none; color: var(--accent-ink);
    font-size: 13px; font-weight: 650; cursor: pointer; padding: 2px 6px;
}
.manage-affiliation-modal .affiliate-link-btn:hover { text-decoration: underline; }
.manage-affiliation-modal .affiliate-link-btn:disabled { color: var(--text-muted); cursor: default; text-decoration: none; }
.manage-affiliation-modal .affiliate-link-btn.affiliate-link-danger { color: var(--danger); }

/* Footer — sticks to the bottom of the scrolling content */
.manage-affiliation-modal .affiliations-footer {
    position: sticky;
    bottom: 0;
    /* Flush to the bottom edge. The old -22px bottom margin pulled it up off the
       edge and let content render in the gap below it. */
    margin: 8px -24px 0;
    padding: 14px 24px 22px;
    background: var(--surface);
    border-top: 1px solid var(--border);
    /* Opaque and above the scrolling list, so nothing bleeds through. */
    z-index: 2;
}
.manage-affiliation-modal .purchase-progress { margin: 14px 0 6px; }
.manage-affiliation-modal .purchase-progress-bar {
    height: 4px; border-radius: 3px; background: var(--border);
    overflow: hidden; position: relative;
}
.manage-affiliation-modal .purchase-progress-bar span {
    position: absolute; left: 0; top: 0; bottom: 0; width: 38%;
    background: var(--accent); border-radius: 3px;
    animation: cuelist-seat-progress 1.15s ease-in-out infinite;
}
@keyframes cuelist-seat-progress {
    0%   { left: -38%; }
    100% { left: 100%; }
}
.manage-affiliation-modal .purchase-progress-text {
    margin-top: 8px; font-size: 13px; color: var(--text-muted); line-height: 1.4;
}
.manage-affiliation-modal .affiliations-footer-total-license { font-size: 14px; font-weight: 650; color: var(--text); margin-bottom: 8px; }
.manage-affiliation-modal .add-licenses-button-container { display: flex; justify-content: space-between; gap: 10px; align-items: center; }
.manage-affiliation-modal .footer-actions-right { display: flex; gap: 10px; align-items: center; }
.manage-affiliation-modal .add-licenses-container { display: flex; flex-direction: column; gap: 10px; text-align: center; }
.manage-affiliation-modal .add-licenses-buttons { display: flex; flex-direction: row; justify-content: center; gap: 8px; }
.manage-affiliation-modal .add-licenses-text { text-align: center; display: flex; align-items: center; justify-content: center; gap: 6px; color: var(--text-muted); font-size: 13px; }

/* Confirm-purchase view */
.manage-affiliation-modal .confirm-purchase-container { display: flex; justify-content: center; text-align: center; }
.manage-affiliation-modal .confirm-text { font-size: 18px; color: var(--text); margin: 24px 0; }

.manage-affiliation-modal .md-error { color: var(--danger); font-size: 12px; }
/* Member who pays for their own subscription: affiliated but never seated. */
.manage-affiliation-modal .affiliate-roster-state.is-self-pay {
    color: var(--text-muted);
    font-style: italic;
}
/* Notes List seat caveat under the invite form (from staging). Uses the modal's
   own muted-text token so it matches the redesign rather than staging's #546e7a. */
</style>


// WEBPACK FOOTER //
// manage-affiliation.vue?730b260c